import axios, { AxiosResponse } from 'axios';
import {
  TCouponBatch,
  TCouponCode,
  TCreateCouponBatchPayload,
  TUpdateCouponBatchPayload,
  TGetCouponBatchesParams,
  TGetCouponBatchesResponse,
  TCreateCouponCodePayload,
  TGetCouponCodesParams,
  TGetCouponCodesResponse,
  TValidateCouponResponse,
  TGetCouponCodeTransactionsParams,
  TGetCouponCodeTransactionsResponse,
} from '@src/types';

export const getCouponBatches = (
  params?: TGetCouponBatchesParams
): Promise<AxiosResponse<TGetCouponBatchesResponse>> => {
  const { page = 1, size = 20, code } = params || {};

  return axios.get('/coupons/batches/', { params: { page, size, code } });
};

export type TCouponCodeSuggestionItem = {
  code: string;
  batch_id: number;
  comment: string;
  label: string;
};

export type TCouponCodeSuggestionsRes = {
  items: TCouponCodeSuggestionItem[];
  more_rows_found: number;
};

export const getCouponCodeSuggestions = (
  q?: string
): Promise<AxiosResponse<TCouponCodeSuggestionsRes>> => {
  return axios.get(`/coupons/suggestions/`, { params: { keyword: q } });
};

export const createCouponBatch = (
  payload: TCreateCouponBatchPayload
): Promise<AxiosResponse<TCouponBatch>> => {
  return axios.post('/coupons/batches/', payload);
};

export const validateCoupon = (
  code: string,
  trading_user_id?: number | null
): Promise<AxiosResponse<TValidateCouponResponse>> => {
  const params = trading_user_id ? { trading_user_id } : {};

  return axios.get(`/coupons/${code}/validate`, { params });
};

export const updateCouponBatch = (
  batchId: number,
  payload: TUpdateCouponBatchPayload
): Promise<AxiosResponse<TCouponBatch>> => {
  return axios.put(`/coupons/batches/${batchId}/`, payload);
};

export const getCouponBatchById = (
  batchId: number
): Promise<AxiosResponse<TCouponBatch>> => {
  return axios.get(`/coupons/batches/${batchId}/`);
};

export const deleteCouponBatch = (
  batchId: number
): Promise<AxiosResponse<void>> => {
  return axios.delete(`/coupons/batches/${batchId}/`);
};

// ── Coupon Codes (individual codes within a batch) ──────────────────────────

export const getCouponCodes = (
  batchId: number,
  params?: TGetCouponCodesParams
): Promise<AxiosResponse<TGetCouponCodesResponse>> => {
  const { page = 1, size = 20 } = params || {};

  return axios.get(`/coupons/batches/${batchId}/codes/`, {
    params: { page, size },
  });
};

export const createCouponCode = (
  batchId: number,
  payload: TCreateCouponCodePayload
): Promise<AxiosResponse<TCouponCode[]>> => {
  return axios.post(`/coupons/batches/${batchId}/codes/`, payload);
};

export const getCouponCodeTransactions = (
  codeId: number,
  params?: TGetCouponCodeTransactionsParams
): Promise<AxiosResponse<TGetCouponCodeTransactionsResponse>> => {
  const { page = 1, size = 20 } = params || {};

  return axios.get(`/coupons/codes/${codeId}/transactions/`, {
    params: { page, size },
  });
};

// NOTE: Delete and update APIs are not yet available — these are mock no-ops
// that resolve immediately. Replace with real calls once the backend exposes them.
export const deleteCouponCode = (
  _batchId: number,
  _codeId: number
): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, 300));
};

export const updateCouponCode = (
  _batchId: number,
  _codeId: number,
  _payload: Partial<TCreateCouponCodePayload>
): Promise<AxiosResponse<TCouponCode>> => {
  return new Promise((_, reject) =>
    setTimeout(
      () => reject(new Error('Update coupon code API not yet available')),
      300
    )
  );
};

// Legacy function aliases for backward compatibility
export const getCoupons = getCouponBatches;
export const createCoupon = createCouponBatch;
export const updateCoupon = updateCouponBatch;
export const getCouponByIdOrCode = getCouponBatchById;
export const deleteCoupon = deleteCouponBatch;
